import { AnimatePresence, m } from 'framer-motion';
import { Fragment } from 'react';
import { opacityAnimation } from '@ui/animation/opacity-animation';
import { Skeleton } from '@ui/skeleton/skeleton';
import { useProducts } from '@common/billing/pricing-table/use-products';
import { Product } from '@common/billing/product';
import {
  findBestPrice,
  UpsellBillingCycle,
} from '@common/billing/pricing-table/find-best-price';
import { useAuth } from '@common/auth/use-auth';
import clsx from 'clsx';
import { Chip } from '@ui/forms/input-field/chip-field/chip';
import { Trans } from '@ui/i18n/trans';
import { FormattedPrice } from '@common/billing/formatted-price';
import { Button } from '@ui/buttons/button';
import { Link } from 'react-router';
import { setInLocalStorage } from '@ui/utils/hooks/local-storage';
import { ProductFeatureList } from '@common/billing/pricing-table/product-feature-list';

interface PricingTableProps {
  selectedCycle: UpsellBillingCycle;
  className?: string;
  productLoader?: string;
  // when provided, only these products are shown (in this order),
  // instead of every product returned by the API
  productIds?: string[];
  // className for each card's grid column wrapper. Defaults to the
  // responsive columns used on the full-width /pricing page; pass
  // something like "col-12" when embedding the table in a narrow slot
  // so cards aren't squeezed by bootstrap's percentage-based columns.
  cardColumnClassName?: string;
}
export function PricingTable({
  selectedCycle,
  className,
  productLoader,
  productIds,
  cardColumnClassName = 'col-12 col-md-6 col-sm-6 col-lg-4 mb-4',
}: PricingTableProps) {
  const query = useProducts(productLoader);
  const products = productIds?.length
    ? (productIds
        .map(id => query.data?.products.find(p => p.id.toString() === id))
        .filter(Boolean) as Product[])
    : query.data?.products;
  return (
    // <div className={clsx(
    //   'flex flex-col items-stretch gap-24 overflow-x-auto overflow-y-visible pb-20 md:flex-row md:justify-center',
    //   className,
    // )}
    // >
    //   <AnimatePresence initial={false} mode="wait">
    //     {query.data ? (
    //       <PlanList
    //         key="plan-list"
    //         plans={query.data.products}
    //         selectedPeriod={selectedCycle}
    //       />
    //     ) : (
    //       <SkeletonLoader key="skeleton-loader" />
    //     )}
    //   </AnimatePresence>
    // </div>

    <div>
      <AnimatePresence initial={false} mode="wait">
        {query.data ? (
          <div className="row justify-content-center">
            {(products ?? []).map((product, index) => (
              <div key={index} className={cardColumnClassName}>
                <PlanList
                  plans={[product]}
                  selectedPeriod={selectedCycle}
                />
              </div>
            ))}
          </div>
        ) : (
          <SkeletonLoader key="skeleton-loader" />
        )}
      </AnimatePresence>
    </div>
  );
}

interface PlanListProps {
  plans: Product[];
  selectedPeriod: UpsellBillingCycle;
}
function PlanList({ plans, selectedPeriod }: PlanListProps) {
  const { isLoggedIn, isSubscribed } = useAuth();
  const filteredPlans = plans.filter(plan => !plan.hidden);
  return (
    <Fragment>
      {filteredPlans.map((plan, index) => {
        const isFirst = index === 0;
        const isLast = index === filteredPlans.length - 1;
        const price = findBestPrice(selectedPeriod, plan.prices);

        let upgradeRoute;
        if (!isLoggedIn) {
          upgradeRoute = `/register?redirectFrom=pricing`;
        }
        if (isSubscribed) {
          upgradeRoute = `/change-plan/${plan.id}/${price?.id}/confirm`;
        }
        if (isLoggedIn && !plan.free) {
          upgradeRoute = `/checkout/${plan.id}/${price?.id}`;
        }

        return (
          <div key={plan.id} className='wrapper' data-aos="fade-up">
            <m.div
              // key={plan.id}  
              {...opacityAnimation}
              className={clsx(
                'w-full first_plan',
                isFirst && 'ml-auto',
                isLast && 'mr-auto',
              )}
            >
              <div className="mb-20">
                {plan.recommended && (
                  <Chip radius="rounded" size="sm" className="mb-20 w-min">
                    <Trans message="Most popular" />
                  </Chip>
                )}
                <div className="mb-12 text-xl font-semibold">
                  <Trans message={plan.name} />
                </div>
                <div className="text-sm text-muted">
                  <Trans message={plan.description} />
                </div>
              </div>
              <div>
                {price ? (
                  <FormattedPrice
                    priceClassName="font-bold text-4xl price_heads"
                    periodClassName="text-muted text-xs"
                    variant="separateLine"
                    price={price}
                  />
                ) : (
                  <div className="text-4xl font-bold free_heads">
                    <Trans message="Free" />
                  </div>
                )}
                <ProductFeatureList product={plan} />
                <div className="mt-20 pricing_btn">
                  <Button
                    variant={plan.recommended ? 'flat' : 'outline'}
                    color="primary"
                    className="w-full"
                    size="md"
                    elementType={upgradeRoute ? Link : undefined}
                    disabled={!upgradeRoute}
                    onClick={() => {
                      if (isLoggedIn || !price || !plan) return;
                      setInLocalStorage('be.onboarding.selected', {
                        productId: plan.id,
                        priceId: price.id,
                      });
                    }}
                    to={upgradeRoute}
                  >
                    {plan.free ? (



                      <Trans message="Get started" />
                    ) : (
                      <Trans message="Upgrade" />
                    )}
                  </Button>
                </div>
              </div>
            </m.div>
          </div>

        );
      })}
    </Fragment>
  );
}

function SkeletonLoader() {
  return (
    <Fragment>
      <PlanSkeleton key="skeleton-1" />
      <PlanSkeleton key="skeleton-2" />
      <PlanSkeleton key="skeleton-3" />
    </Fragment>
  );
}

function PlanSkeleton() {
  return (
    <m.div
      {...opacityAnimation}
      className="w-full rounded-lg border px-28 py-90 shadow-lg md:max-w-350"
    >
      <Skeleton className="my-10" />
      <Skeleton className="mb-40" />
      <Skeleton className="mb-40 h-30" />
      <Skeleton className="mb-40 h-40" />
      <Skeleton className="mb-20" />
      <Skeleton />
      <Skeleton />
    </m.div>
  );
}
